import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, User, Mail, Phone, Building, MessageSquare, CheckCircle, Settings } from 'lucide-react';
import { sendContactFormEmail, isEmailJsConfigured } from '../../utils/emailjsContact';
import toast from 'react-hot-toast';

const EnquiryForm = ({ serviceName, onSuccess = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    companyName: '',
    serviceInterest: serviceName || '',
    message: '',
    source: 'WEBSITE'
  });
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!isEmailJsConfigured()) {
        toast.error(
          'Email is not configured. Add REACT_APP_EMAILJS_* variables to .env and restart the app.'
        );
        return;
      }

      await sendContactFormEmail(formData);

      setSubmitted(true);
      toast.success('Your enquiry has been submitted successfully! We will contact you soon.');

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      const msg =
        error?.message ||
        error?.text ||
        'Failed to submit enquiry. Please try again or contact us by phone.';
      toast.error(typeof msg === 'string' ? msg : 'Failed to submit enquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      companyName: '',
      serviceInterest: serviceName || '',
      message: '',
      source: 'WEBSITE'
    });
    setSubmitted(false);
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-lg p-8 text-center"
      >
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Thank You!
        </h3>
        <p className="text-gray-600 mb-6">
          Your enquiry has been submitted successfully. Our team will contact you within 24 hours.
        </p>
        <button
          onClick={resetForm}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Submit Another Enquiry
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="grid grid-cols-1 lg:grid-cols-3 gap-0 rounded-lg overflow-hidden shadow-lg w-full max-w-6xl mx-auto"
    >
      {/* Left Column - Service Information */}
      <div className="bg-gradient-to-br from-blue-800 to-blue-900 p-4 sm:p-6 lg:p-8 text-white lg:col-span-1">
        <div className="text-center">
          <h3 className="text-xl sm:text-2xl font-bold uppercase tracking-wide mb-6 sm:mb-8">
            {serviceName || 'Our Services'}
          </h3>
          
          {/* Service Icons */}
          <div className="flex flex-row justify-center gap-4 sm:gap-8 mb-6 sm:mb-8">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-full flex items-center justify-center mb-3 border-2 border-blue-300">
                <Settings className="w-8 h-8 sm:w-10 sm:h-10 text-blue-600" />
              </div>
              <span className="text-white text-sm font-medium">Expert Service</span>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-full flex items-center justify-center mb-3 border-2 border-blue-300">
                <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-blue-600" />
              </div>
              <span className="text-white text-sm font-medium">Quality Assured</span>
            </div>
          </div>
          
          <div className="space-y-3 text-left">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-blue-300 rounded-full mr-3 flex-shrink-0"></div>
              <span className="text-blue-100 text-sm">Professional consultation</span>
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-blue-300 rounded-full mr-3 flex-shrink-0"></div>
              <span className="text-blue-100 text-sm">Customized solutions</span>
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-blue-300 rounded-full mr-3 flex-shrink-0"></div>
              <span className="text-blue-100 text-sm">24/7 support</span>
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 bg-blue-300 rounded-full mr-3 flex-shrink-0"></div>
              <span className="text-blue-100 text-sm">Fast response time</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Column - Enquiry Form */}
      <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 sm:p-6 lg:p-8 lg:col-span-2">
        <div className="mb-4 sm:mb-6">
          <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-900 uppercase tracking-wide mb-2">
            Get Quote
          </h3>
          <p className="text-blue-700 text-xs sm:text-sm">
            Please provide below information to get a quote.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-3">
          {/* First Row - Name and Email */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full px-0 py-2 border-0 border-b-2 border-blue-300 bg-transparent text-blue-900 placeholder-blue-400 focus:outline-none focus:border-blue-600 transition-colors text-sm sm:text-base"
                placeholder="First Name *"
              />
            </div>
            <div>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-0 py-2 border-0 border-b-2 border-blue-300 bg-transparent text-blue-900 placeholder-blue-400 focus:outline-none focus:border-blue-600 transition-colors text-sm sm:text-base"
                placeholder="Email *"
              />
            </div>
          </div>

          {/* Second Row - Phone and Company */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-0 py-2 border-0 border-b-2 border-blue-300 bg-transparent text-blue-900 placeholder-blue-400 focus:outline-none focus:border-blue-600 transition-colors text-sm sm:text-base"
                placeholder="Phone number *"
              />
            </div>
            <div>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                className="w-full px-0 py-2 border-0 border-b-2 border-blue-300 bg-transparent text-blue-900 placeholder-blue-400 focus:outline-none focus:border-blue-600 transition-colors text-sm sm:text-base"
                placeholder="Company Name"
              />
            </div>
          </div>

          {/* Third Row - Message Field */}
          <div>
            <textarea
              id="message"
              name="message"
              required
              rows={2}
              value={formData.message}
              onChange={handleChange}
              className="w-full px-0 py-3 border-0 border-b-2 border-blue-300 bg-transparent text-blue-900 placeholder-blue-400 focus:outline-none focus:border-blue-600 transition-colors resize-none text-sm sm:text-base"
              placeholder="Describe your requirements... *"
            />
          </div>

          {/* Submit Button */}
          <div className="pt-4">
            <motion.button
              type="submit"
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-200 text-sm sm:text-base"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-5 w-5 mr-2" />
                  Get Quote
                </>
              )}
            </motion.button>
          </div>
        </form>
      </div>
    </motion.div>
  );
};

export default EnquiryForm;
